import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabaseClient';

const TOKENS = [
    {
        symbol: 'ETH',
        name: 'Ethereum',
        icon: '⟠',
        coingeckoId: 'ethereum',
        fallbackPrice: 3000,
        moonpayCode: 'eth',
    },
    {
        symbol: 'USDT',
        name: 'Tether USD',
        icon: '₮',
        coingeckoId: 'tether',
        fallbackPrice: 1.0,
        moonpayCode: 'usdt',
    },
    {
        symbol: 'USDC',
        name: 'USD Coin',
        icon: '$',
        coingeckoId: 'usd-coin',
        fallbackPrice: 1.0,
        moonpayCode: 'usdc',
    },
];

const QUICK_AMOUNTS = [50, 100, 250];
const MIN_USD = 30;
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

async function fetchPrices() {
    const ids = TOKENS.map(t => t.coingeckoId).join(',');
    try {
        const res = await fetch(
            `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`,
            { signal: AbortSignal.timeout(6000) }
        );
        if (!res.ok) throw new Error('CoinGecko error');
        const json = await res.json();
        const prices = {};
        TOKENS.forEach(t => {
            prices[t.symbol] = json?.[t.coingeckoId]?.usd ?? t.fallbackPrice;
        });
        return prices;
    } catch {
        const fallback = {};
        TOKENS.forEach(t => { fallback[t.symbol] = t.fallbackPrice; });
        return fallback;
    }
}

function makeUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * MoonPay Buy flow — opens the MoonPay overlay widget with the user's
 * platform wallet address pre-filled (Trust Wallet style). The purchase
 * is credited to the spendable USD balance by the /api/moonpay-webhook
 * once MoonPay reports it completed; this modal only tracks status by
 * polling its own moonpay_transactions row.
 */
function BuyModal({ walletAddress, userId, onClose, onCompleted }) {
    const [selected, setSelected]   = useState('ETH');
    const [usdAmount, setUsdAmount] = useState('100');
    const [prices, setPrices]       = useState(null);
    // 'form' → 'opening' → 'widget' → 'done' | 'failed'
    const [phase, setPhase]         = useState('form');
    const [errorMsg, setErrorMsg]   = useState('');

    const pollRef = useRef(null);
    const completedRef = useRef(false);

    useEffect(() => {
        fetchPrices().then(setPrices);
        return () => clearInterval(pollRef.current);
    }, []);

    const token  = TOKENS.find(t => t.symbol === selected);
    const price  = prices?.[selected];
    const amount = parseFloat(usdAmount);
    const validAmount = Number.isFinite(amount) && amount >= MIN_USD;
    const estimate = validAmount && price
        ? (amount / price).toLocaleString('en-US', {
            maximumFractionDigits: selected === 'ETH' ? 6 : 2,
        })
        : null;

    const startPolling = (externalTransactionId) => {
        const startedAt = Date.now();
        pollRef.current = setInterval(async () => {
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                clearInterval(pollRef.current);
                return;
            }
            const { data } = await supabase
                .from('moonpay_transactions')
                .select('status')
                .eq('external_transaction_id', externalTransactionId)
                .maybeSingle();
            if (data?.status === 'completed' && !completedRef.current) {
                completedRef.current = true;
                clearInterval(pollRef.current);
                setPhase('done');
                onCompleted?.();
            } else if (data?.status === 'failed') {
                clearInterval(pollRef.current);
                setPhase('failed');
            }
        }, POLL_INTERVAL_MS);
    };

    const handleContinue = async () => {
        if (!validAmount || !walletAddress || !userId) return;
        setErrorMsg('');
        setPhase('opening');
        try {
            const { loadMoonPay } = await import('@moonpay/moonpay-js');
            const moonPay = await loadMoonPay();
            const externalTransactionId = makeUuid();

            const sdk = moonPay({
                flow: 'buy',
                environment: process.env.REACT_APP_MOONPAY_ENVIRONMENT === 'production'
                    ? 'production'
                    : 'sandbox',
                variant: 'overlay',
                params: {
                    apiKey: process.env.REACT_APP_MOONPAY_PUBLISHABLE_KEY,
                    currencyCode: token.moonpayCode,
                    baseCurrencyCode: 'usd',
                    baseCurrencyAmount: String(amount),
                    walletAddress,
                    externalCustomerId: userId,
                    externalTransactionId,
                    theme: 'dark',
                    colorCode: '#19bfb1',
                },
            });

            // MoonPay requires the URL to be signed server-side whenever a
            // walletAddress is passed. The sign endpoint also verifies the
            // address belongs to this user and records the purchase intent.
            const urlForSignature = sdk.generateUrlForSigning();
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            if (!accessToken) throw new Error('Your session has expired — please sign in again.');

            const res = await fetch('/api/moonpay-sign', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ url: urlForSignature }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || 'Could not start the purchase. Please try again.');

            sdk.updateSignature(body.signature);
            sdk.show();
            setPhase('widget');
            startPolling(externalTransactionId);
        } catch (err) {
            console.error('Buy flow error:', err);
            setErrorMsg(err.message || 'Something went wrong. Please try again.');
            setPhase('form');
        }
    };

    return (
        <>
            <div style={s.overlay} onClick={onClose} aria-hidden="true" />
            <div style={s.sheet} role="dialog" aria-label="Buy crypto">
                <div style={s.handle} aria-hidden="true" />

                {/* Header */}
                <div style={s.header}>
                    <div style={s.headerIcon}>
                        <i className="bx bx-credit-card" style={{ color: '#a78bfa', fontSize: 22 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={s.title}>Buy Crypto</div>
                        <div style={s.subtitle}>Pay by card via MoonPay</div>
                    </div>
                    <button onClick={onClose} style={s.closeBtn} aria-label="Close">
                        <i className="bx bx-x" style={{ fontSize: 20 }} />
                    </button>
                </div>

                {phase === 'done' ? (
                    <div style={s.statusWrap}>
                        <div style={{ ...s.statusIcon, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)' }}>
                            <i className="bx bx-check-circle" style={{ color: '#10b981', fontSize: 34 }} />
                        </div>
                        <div style={s.statusTitle}>Purchase complete!</div>
                        <p style={s.statusMsg}>
                            Your purchase has been credited to your balance.
                        </p>
                        <button style={s.primaryBtn} onClick={onClose}>Done</button>
                    </div>
                ) : phase === 'failed' ? (
                    <div style={s.statusWrap}>
                        <div style={{ ...s.statusIcon, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }}>
                            <i className="bx bx-x-circle" style={{ color: '#ef4444', fontSize: 34 }} />
                        </div>
                        <div style={s.statusTitle}>Purchase failed</div>
                        <p style={s.statusMsg}>
                            The payment didn't go through. You have not been charged — please try again.
                        </p>
                        <button style={s.primaryBtn} onClick={() => setPhase('form')}>Try Again</button>
                    </div>
                ) : phase === 'widget' ? (
                    <div style={s.statusWrap}>
                        <div style={{ ...s.statusIcon, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)' }}>
                            <i className="bx bx-time-five" style={{ color: '#a78bfa', fontSize: 34 }} />
                        </div>
                        <div style={s.statusTitle}>Complete your purchase</div>
                        <p style={s.statusMsg}>
                            Finish the payment in the MoonPay window. Once it's confirmed,
                            funds are credited to your balance automatically — you can close
                            this screen at any time.
                        </p>
                        <button style={s.secondaryBtn} onClick={onClose}>Close</button>
                    </div>
                ) : (
                    <>
                        {/* Token selector */}
                        <div style={s.tokenRow}>
                            {TOKENS.map(t => (
                                <button
                                    key={t.symbol}
                                    onClick={() => setSelected(t.symbol)}
                                    style={{ ...s.tokenBtn, ...(selected === t.symbol ? s.tokenBtnActive : {}) }}
                                >
                                    <span style={s.tokenIcon}>{t.icon}</span>
                                    <div>
                                        <div style={s.tokenSymbol}>{t.symbol}</div>
                                        <div style={s.tokenName}>{t.name}</div>
                                    </div>
                                    {selected === t.symbol && (
                                        <i className="bx bx-check-circle" style={s.tokenCheck} />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Amount */}
                        <div style={s.amountLabel}>Amount (USD)</div>
                        <div style={s.amountBox}>
                            <span style={s.amountPrefix}>$</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                min={MIN_USD}
                                value={usdAmount}
                                onChange={e => setUsdAmount(e.target.value)}
                                style={s.amountInput}
                                aria-label="Amount in USD"
                            />
                        </div>
                        <div style={s.quickRow}>
                            {QUICK_AMOUNTS.map(q => (
                                <button
                                    key={q}
                                    onClick={() => setUsdAmount(String(q))}
                                    style={{
                                        ...s.quickBtn,
                                        ...(usdAmount === String(q) ? s.quickBtnActive : {}),
                                    }}
                                >
                                    ${q}
                                </button>
                            ))}
                        </div>

                        {estimate ? (
                            <div style={s.rateRow}>
                                <i className="bx bx-line-chart" style={{ color: '#19bfb1', marginRight: 6 }} />
                                ≈ {estimate} {selected}
                            </div>
                        ) : (
                            <div style={s.rateRow}>
                                <i className="bx bx-info-circle" style={{ color: 'rgba(255,255,255,0.4)', marginRight: 6 }} />
                                Minimum purchase ${MIN_USD}
                            </div>
                        )}

                        {errorMsg && (
                            <div style={s.errorBox}>
                                <i className="bx bx-error-circle" style={{ color: '#ef4444', marginRight: 6, flexShrink: 0 }} />
                                <span>{errorMsg}</span>
                            </div>
                        )}

                        <button
                            onClick={handleContinue}
                            disabled={!validAmount || phase === 'opening'}
                            style={{
                                ...s.primaryBtn,
                                ...(!validAmount || phase === 'opening' ? s.primaryBtnDisabled : {}),
                            }}
                        >
                            {phase === 'opening'
                                ? 'Opening MoonPay…'
                                : `Continue with MoonPay`}
                        </button>

                        <div style={s.note}>
                            <i className="bx bx-shield-quarter" style={{ color: '#19bfb1', marginRight: 6, flexShrink: 0 }} />
                            <span>
                                Payment is processed securely by <strong>MoonPay</strong>. The{' '}
                                {token?.name} you buy is delivered to your Sigma Bank wallet and
                                credited to your balance as USD.
                            </span>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

const s = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 200,
    },
    sheet: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, #0f0a4a 0%, #0c0836 100%)',
        borderRadius: '20px 20px 0 0',
        padding: '0 20px 40px',
        zIndex: 201,
        animation: 'sheetUp 0.3s ease',
        maxHeight: '92vh',
        overflowY: 'auto',
    },
    handle: {
        width: 36,
        height: 4,
        background: 'rgba(255,255,255,0.2)',
        borderRadius: 2,
        margin: '12px auto 0',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '20px 0 16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        marginBottom: 20,
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        background: 'rgba(167,139,250,0.15)',
        border: '1px solid rgba(167,139,250,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
    },
    subtitle: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
        marginTop: 2,
    },
    closeBtn: {
        background: 'rgba(255,255,255,0.08)',
        border: 'none',
        borderRadius: 8,
        color: 'rgba(255,255,255,0.7)',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
    },
    tokenRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 20,
    },
    tokenBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14,
        cursor: 'pointer',
        color: '#fff',
        textAlign: 'left',
        transition: 'all 0.2s',
    },
    tokenBtnActive: {
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.4)',
    },
    tokenIcon: {
        fontSize: 20,
        width: 36,
        height: 36,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 1,
        textAlign: 'center',
        paddingTop: 6,
    },
    tokenSymbol: {
        fontSize: 14,
        fontWeight: 700,
        color: '#fff',
    },
    tokenName: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        marginTop: 2,
    },
    tokenCheck: {
        marginLeft: 'auto',
        color: '#19bfb1',
        fontSize: 18,
    },
    amountLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        marginBottom: 8,
    },
    amountBox: {
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        padding: '4px 14px',
        marginBottom: 10,
    },
    amountPrefix: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 18,
        fontWeight: 700,
        marginRight: 6,
    },
    amountInput: {
        flex: 1,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        color: '#fff',
        fontSize: 20,
        fontWeight: 700,
        padding: '10px 0',
        width: '100%',
    },
    quickRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 8,
        marginBottom: 14,
    },
    quickBtn: {
        padding: '9px 0',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        color: 'rgba(255,255,255,0.7)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    quickBtnActive: {
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.4)',
        color: '#19bfb1',
    },
    rateRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 13,
        marginBottom: 14,
    },
    errorBox: {
        display: 'flex',
        alignItems: 'flex-start',
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.25)',
        borderRadius: 12,
        padding: '12px 14px',
        color: 'rgba(255,255,255,0.75)',
        fontSize: 12,
        lineHeight: 1.6,
        marginBottom: 14,
    },
    primaryBtn: {
        width: '100%',
        padding: '14px',
        background: '#19bfb1',
        border: 'none',
        borderRadius: 12,
        color: '#0c0836',
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        marginBottom: 14,
    },
    primaryBtnDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    secondaryBtn: {
        width: '100%',
        padding: '13px',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 12,
        color: 'rgba(255,255,255,0.85)',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
    note: {
        display: 'flex',
        alignItems: 'flex-start',
        background: 'rgba(25,191,177,0.06)',
        border: '1px solid rgba(25,191,177,0.15)',
        borderRadius: 12,
        padding: '12px 14px',
        color: 'rgba(255,255,255,0.65)',
        fontSize: 12,
        lineHeight: 1.6,
    },
    statusWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '16px 8px 8px',
    },
    statusIcon: {
        width: 64,
        height: 64,
        borderRadius: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    statusTitle: {
        color: '#fff',
        fontSize: 17,
        fontWeight: 700,
        marginBottom: 8,
    },
    statusMsg: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 13,
        lineHeight: 1.6,
        marginBottom: 20,
        maxWidth: 300,
    },
};

export default BuyModal;
